import React, { useEffect, useMemo, useState } from 'react';
import MaterialTable, {
 MTableToolbar,
  MTableHeader, MTableBody,
} from 'material-table';
import { useParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import {
  getStickyColumns, localization, modifyDefaultOptions, objectToCols,
} from '../../helpers/table';
import {
 getFieldRenderFunction, indexValueRenderer, renderEditMoneyComponent, renderNullFloatValues, renderNullThousandsFloatValues,
} from '../../pages/index/Shared';
import { tableIcons } from '../../helpers';
import MTableFilterRow from '../material-table-overrides/MTableFilterRow';
import DownloadLink from '../common/DownloadLink';
import { dateRenderer } from '../../services/formatSvc';
import { getCols, getTitleByParams } from '../../helpers/unified';
import LoadingTable from '../table/LoadingTable';
import useAPI from '../../hooks/useAPI';
import useNavProject from '../../hooks/useNavProject';
import { BaseRequests } from '../../requests/BaseRequests';

export const UnifiedIndex = (props) => {
  const {
    filterState, dataState, months, canExport, years, ...rest
  } = props;

  const { originalData } = { ...dataState };
  const { filterYears, setFilterYears } = { ...filterState };
  const {
    stratId, projId, policyId, portfolioId,
  } = useParams();
  const navProject = useNavProject();
  const [isLoaded, setIsLoaded] = useState(!!originalData.length);
  const [title, setTitle] = useState('');
  const api = useAPI();

  const colsLeft = useMemo(()=>rest?.firstCols ?? getCols(originalData, {
    stratId, projId, policyId, portfolioId,
  }, null, navProject), [navProject, originalData]);

  useEffect(async () => {
    const title = await getTitleByParams({
      stratId, projId, policyId, portfolioId,
    }, api);
    setTitle(title);
  }, []);

  useEffect(() => {
    Array.isArray(originalData) && setIsLoaded(true);
  }, [originalData]);

  const handleChange = (e) => {
    const yearValue = parseInt(e.target.value);
    const newFilterYears = filterYears.includes(yearValue)
      ? filterYears.filter((item) => item !== yearValue).sort()
      : [...filterYears, yearValue].sort();
    setFilterYears(newFilterYears);
  };

  const getColumns = () => {
    const byYearsColsFactory = (year) => {
      const result = [];
      months.forEach((m) => {
        result.push({
          title: `Факт на 01.${m}.${year}`,
          field: `fact${m}_${year}`,
          render: (rowData) => indexValueRenderer(rowData, rowData[`fact${m}_${year}`]),
          filterCellStyle: { minWidth: '12%' },
        });
      });

      return result;
    };

    const colsMiddle = filterYears.reduce(
      (acc, year) => [
        ...acc,
        {
          title: `План ${year}`,
          field: `plan${year}`,
          editComponent: renderEditMoneyComponent,
          render: (rowData) => indexValueRenderer(rowData.unit, rowData[`plan${year}`]),
          filterCellStyle: { minWidth: '14%' },
        },
        ...byYearsColsFactory(year),
        {
          title: `Факт ${year}`,
          field: `fact${year}`,
          render: (rowData) => indexValueRenderer(rowData.unit, rowData[`fact${year}`]),
          filterCellStyle: { minWidth: '14%' },
        },
      ],
      [],
    );

    return [...getStickyColumns(colsLeft, 0), ...colsMiddle];
  };

  return (
    <div style={styledTable}>
      <LoadingTable loadingObject={{ isLoaded }}>
        <MaterialTable
          options={modifyDefaultOptions({
            columnsButton: true,
            maxBodyHeight: 'calc(100vh - 442px)',
            rowStyle: (rowData) => ({
              backgroundColor: !rowData.parentId ? '#f5f5ff' : '#FFF',
            }),
            paging: false,
            headerStyle: { height: 50, position: 'sticky' },
            filterCellStyle: {
              position: 'sticky',
              top: 54,
              background: 'white',
            },
          })}
          icons={tableIcons}
          localization={localization}
          columns={objectToCols(getColumns())}
          data={originalData}
          components={Object.assign({
            Header: (props) => <MTableHeader {...props} isTreeData={rest?.hasChildData} />,
            Body: (props) => <MTableBody {...props} isTreeData={rest?.hasChildData} />,
            FilterRow: (props) => <MTableFilterRow {...props} isTreeData={rest?.hasChildData} />,
            Toolbar: (props) => (
                <div>
                  <div style={styledToolbarContainer}>
                    <div style={{ width: '100%' }}>
                      <MTableToolbar {...props} />
                    </div>
                    {canExport && (
                        <div style={{ flex: 'none', marginLeft: 16 }}>
                          <DownloadLink variant="outline-primary" url={BaseRequests.getUrlSearch(`/reports/fixedPcs/0/export`,{
                              years:JSON.stringify(filterYears),
                              policyId,
                              portfId: portfolioId,
                              strategyId: stratId,
                              projId,
                          })} filename={`Отчет по унифицированным показателям_${dateRenderer(new Date())}.xlsx`}>
                            Выгрузить в Excel
                          </DownloadLink>
                        </div>
                    )}
                  </div>
                  <div className="filters-container" style={{ paddingRight: 0 }}>
                    <div className="year-checkboxes">
                      {years.map((year, i) => (
                          <label key={year}>
                            <input type="checkbox" value={year} onChange={handleChange} checked={filterYears.includes(year)} />
                            <span className="tag">{year}</span>
                          </label>
                      ))}
                    </div>

                    {rest?.isExpandable && <div style={{ flex: 'none', marginLeft: 16 }}>
                      <Button style={{ flex: 'none', marginLeft: 16 }} onClick={rest?.toggleExpand}>
                        {rest?.allRowsExpanded ? 'Свернуть' : 'Развернуть'}
                        {' '}
                        все
                      </Button>
                    </div>}
                  </div>
                </div>
            ),
          },rest?.tableComponents ?? {})}
          title={title}
          parentChildData={rest?.hasChildData ? ((row, rows) => rows.find((a) => a.id === row.parentId)) : null}
        />
      </LoadingTable>
    </div>
  );
}

const styledTable = {
  maxWidth: '100%',
  marginTop: 32,
};

const styledToolbarContainer = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

export default UnifiedIndex;
